import { useEffect, useState } from "react";
import { T, base } from "../lib/theme";
import { apiFetch } from "../lib/api";

// ── Icons ──────────────────────────────────────────────────────────
const IcoUsers  = ({c}) => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const IcoBcast  = ({c}) => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.22C1.6 2.1 2.43 1.18 3.55 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 16.92z"/></svg>;
const IcoChat   = ({c}) => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>;
const IcoMedia  = ({c}) => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>;
const IcoBook   = ({c}) => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>;
const IcoRobot  = ({c}) => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8"><rect x="3" y="8" width="18" height="13" rx="2"/><path d="M12 3v5"/><circle cx="12" cy="3" r="1.5"/><path d="M7 13h2"/><path d="M15 13h2"/><path d="M9 17h6"/></svg>;
const IcoZap    = ({c}) => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>;

// ── Usage bar ──────────────────────────────────────────────────────
const UsageBar = ({ used, limit, color }) => {
  const pct = limit === -1 ? 0 : Math.min(100, Math.round(((used || 0) / (limit || 1)) * 100));
  const col = pct >= 90 ? T.rose : pct >= 70 ? T.amber : color || T.indigo;
  return (
    <div style={{ width: "100%", height: 5, borderRadius: 99, background: T.border1, overflow: "hidden", marginTop: 7 }}>
      <div style={{ height: "100%", width: `${pct}%`, background: col, borderRadius: 99, transition: "width 0.7s ease" }} />
    </div>
  );
};

// ── Single limit row ───────────────────────────────────────────────
const LimitRow = ({ Ico, label, used, limit, color, hint }) => {
  const pct = limit === -1 ? 0 : Math.min(100, Math.round(((used || 0) / (limit || 1)) * 100));
  const col = pct >= 90 ? T.rose : pct >= 70 ? T.amber : color;
  const exhausted = limit !== -1 && used >= limit;
  return (
    <div style={{ padding: "14px 16px", borderRadius: 12, background: T.bg2, border: `1px solid ${exhausted ? T.rose+"44" : T.border1}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Ico c={color} />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text1 }}>{label}</div>
            {hint && <div style={{ fontSize: 10, color: T.text3, marginTop: 1 }}>{hint}</div>}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 13, fontWeight: 700, fontFamily: base.mono, color: col }}>
            {used ?? "—"} <span style={{ fontSize: 11, fontWeight: 400, color: T.text3 }}>/ {limit === -1 ? "∞" : (limit ?? "—")}</span>
          </div>
          {limit !== -1 && (
            <div style={{ fontSize: 10, color: col, marginTop: 1 }}>
              {exhausted ? "Limit reached" : `${pct}% used`}
            </div>
          )}
          {limit === -1 && <div style={{ fontSize: 10, color: T.indigo, marginTop: 1 }}>Unlimited</div>}
        </div>
      </div>
      <UsageBar used={used} limit={limit} color={color} />
    </div>
  );
};

// ── Plan badge ─────────────────────────────────────────────────────
const PlanBadge = ({ plan }) => {
  const cfg = {
    free:       { color: T.sky,    bg: "rgba(56,189,248,0.1)",    label: "FREE"    },
    starter:    { color: T.indigo, bg: "rgba(99,102,241,0.12)",   label: "STARTER" },
    growth:     { color: T.violet, bg: "rgba(139,92,246,0.12)",   label: "GROWTH"  },
    pro:        { color: T.amber,  bg: "rgba(245,158,11,0.12)",   label: "PRO"     },
    enterprise: { color: T.teal,   bg: "rgba(20,184,166,0.12)",   label: "ENTERPRISE" },
  }[plan] || { color: T.sky, bg: "rgba(56,189,248,0.1)", label: (plan||"FREE").toUpperCase() };
  return (
    <span style={{ padding: "4px 12px", borderRadius: 20, background: cfg.bg, color: cfg.color, fontSize: 11, fontWeight: 800, border: `1px solid ${cfg.color}33`, letterSpacing: "0.06em" }}>
      {cfg.label}
    </span>
  );
};

export default function PlanPage({ token, setPage }) {
  const [info,    setInfo]    = useState(null);
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiFetch("/api/plan-info", {}, token),
      apiFetch("/api/credits",   {}, token),
    ]).then(([p, c]) => {
      setInfo(p);
      setCredits(c);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [token]);

  const plan    = info?.plan || "free";
  const isFreeLikePlan = plan === "free" || plan === "trial";
  const limits  = info?.limits || {};
  const usage   = info?.usage  || {};
  const creditLimit = credits?.limit ?? 10;
  const creditUsed  = credits?.used  ?? 0;

  const handleUpgrade = () => {
    window.open("https://zepliq.com/contact", "_blank");
  };

  const limitRows = [
    { key: "maxContacts",   Ico: IcoUsers, label: "Contacts / month",   color: T.indigo, hint: "WhatsApp contacts your bot can store"     },
    { key: "maxBroadcasts", Ico: IcoBcast, label: "Broadcasts / month", color: T.violet, hint: "Broadcast campaigns you can send"          },
    { key: "maxChats",      Ico: IcoChat,  label: "Chats stored",       color: T.sky,    hint: "Maximum stored conversations"              },
    { key: "maxMediaMB",    Ico: IcoMedia, label: "Media uploads (MB)", color: T.amber,  hint: "Total media file storage for auto-replies"  },
    { key: "maxKBChars",    Ico: IcoBook,  label: "Knowledge base",     color: T.teal,   hint: "Characters in your knowledge base"          },
    { key: "aiRatePerHour", Ico: IcoRobot, label: "AI replies / hour",  color: T.rose,   hint: "AI replies allowed per hour"               },
  ];

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "clamp(14px, 4vw, 26px) clamp(14px, 4vw, 28px) 60px" }}>

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: T.text1, marginBottom: 4 }}>Plan & Usage</div>
        <div style={{ fontSize: 13, color: T.text3 }}>Track your usage limits and upgrade your plan</div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: T.text3, display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
          <div style={{ width: 26, height: 26, border: `2px solid ${T.border2}`, borderTopColor: T.indigo, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
          Loading plan info...
        </div>
      ) : (
        <>
          {/* Current plan hero */}
          <div style={{ padding: "20px 24px", borderRadius: 16, background: T.bg2, border: `1px solid ${T.border1}`, marginBottom: 24, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
            <div>
              <div style={{ fontSize: 11, color: T.text3, marginBottom: 7, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em" }}>Current Plan</div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <PlanBadge plan={plan} />
                <span style={{ fontSize: 12, color: T.text3 }}>
                  {isFreeLikePlan ? "Upgrade to unlock more features" :
                   plan === "enterprise" ? "All features unlocked" :
                   "Plan active"}
                </span>
              </div>
            </div>
            {isFreeLikePlan && (
              <button onClick={() => handleUpgrade("Starter")}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 18px", borderRadius: 10, border: "none", cursor: "pointer", fontFamily: base.font, fontSize: 13, fontWeight: 700, background: "linear-gradient(135deg, #6366f1, #8b5cf6)", color: "#fff", transition: "all 0.18s" }}>
                <IcoZap c="#fff" /> Upgrade Plan
              </button>
            )}
          </div>

          {/* AI Credits */}
          <div style={{ padding: "18px 20px", borderRadius: 14, background: T.bg2, border: `1px solid ${T.border1}`, marginBottom: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: "rgba(99,102,241,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <IcoRobot c={T.indigo} />
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text1 }}>AI Insights Credits</div>
              </div>
              <div style={{ fontSize: 11, color: T.text3 }}>
                Resets {credits?.resetAt ? new Date(credits.resetAt).toLocaleDateString([], { month: "short", day: "numeric" }) : "1st of month"}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 8 }}>
              <div style={{ flex: 1, height: 7, borderRadius: 99, background: T.border1, overflow: "hidden" }}>
                <div style={{
                  height: "100%",
                  width: `${creditLimit === -1 ? 0 : Math.min(100, Math.round((creditUsed / (creditLimit || 1)) * 100))}%`,
                  background: creditUsed >= creditLimit && creditLimit !== -1 ? T.rose : "linear-gradient(90deg,#6366f1,#8b5cf6)",
                  borderRadius: 99, transition: "width 0.7s ease",
                }} />
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, fontFamily: base.mono, color: T.indigo, whiteSpace: "nowrap" }}>
                {creditUsed} / {creditLimit === -1 ? "∞" : creditLimit}
              </div>
            </div>
            <div style={{ fontSize: 11, color: T.text3 }}>
              {creditLimit === -1 ? "Unlimited credits" : `${Math.max(0, creditLimit - creditUsed)} credits remaining this month`}
            </div>
          </div>

          {/* Usage limits grid */}
          <div style={{ marginBottom: 30 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text1, marginBottom: 14 }}>Feature Usage</div>
            <div className="grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {limitRows.map(r => (
                <LimitRow key={r.key} Ico={r.Ico} label={r.label} color={r.color} hint={r.hint}
                  used={usage[r.key]} limit={limits[r.key] !== undefined ? limits[r.key] : -1} />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
